import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import NeilMethodSection, { LIFECYCLE } from "@/components/NeilMethodSection";
import HealthcareAssessmentForm from "@/components/HealthcareAssessmentForm";

import { ArrowRight, ShieldCheck, Target, LineChart, Wrench, Expand } from "lucide-react";

const trustPoints = [
  {
    icon: ShieldCheck,
    title: "LESS GUESSWORK",
    copy: "Recommendations are based on documented discovery, not assumptions.",
  },
  {
    icon: Target,
    title: "BETTER FIT",
    copy: "Technology is selected around the environment it has to operate in.",
  },
  {
    icon: LineChart,
    title: "ECONOMIC CLARITY",
    copy: "A labor baseline is established before any ROI comparison is presented.",
  },
  {
    icon: Wrench,
    title: "DEPLOYMENT READINESS",
    copy: "Operational constraints are identified before implementation begins.",
  },
  {
    icon: Expand,
    title: "SCALABILITY",
    copy: "Successful deployments can become repeatable multi-site models.",
  },
];

const NeilMethod = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="The N.E.I.L.™ Method | Neil AI Global Solutions"
        description="The N.E.I.L.™ Method is Neil AI's structured discovery and deployment methodology: Name the Need, Evaluate the Environment, Itemize the Inputs, Lock the Labor Baseline — before any automation is recommended."
        keywords="N.E.I.L. Method, automation assessment, robotics discovery process, Robotics-as-a-Service methodology, autonomous workforce consulting, labor baseline analysis"
      />
      <Navigation />

      <main className="pt-32 md:pt-40">
        <section className="px-4">
          <div className="mx-auto max-w-5xl text-center">
            <p className="mb-4 text-xs font-bold tracking-[0.32em] text-[#D6A84F]">
              POWERED BY THE N.E.I.L.™ METHOD
            </p>
            <h1 className="font-orbitron text-4xl font-bold leading-tight text-foreground md:text-6xl">
              THE N.E.I.L.™ METHOD
            </h1>
            <p className="font-orbitron mx-auto mt-4 max-w-3xl text-lg text-primary md:text-2xl">
              A better way to build an autonomous workforce.
            </p>
            <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Neil AI uses a structured discovery and deployment methodology designed to connect
              automation decisions with actual operational requirements.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="min-h-12 rounded-full bg-gradient-to-r from-[hsl(43_75%_55%)] to-[hsl(43_85%_65%)] px-8 font-semibold text-black hover:opacity-95"
              >
                <a href="#healthcare-assessment">
                  START YOUR N.E.I.L.™ ASSESSMENT
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>

              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="min-h-12 rounded-full border-primary/40 bg-background/50 px-8"
              >
                <Link to="/raas">EXPLORE RaaS</Link>
              </Button>
            </div>
          </div>
        </section>

        <div className="px-4">
          <NeilMethodSection
            contextLabel="THE FOUR DISCOVERY STEPS"
            contextCopy="Automation starts with understanding the operation. Every Neil AI engagement begins with structured discovery before any technology is recommended."
          />
        </div>

        <section className="px-4 py-14 md:py-20" aria-labelledby="lifecycle-heading">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <h2 id="lifecycle-heading" className="font-orbitron text-3xl font-bold text-foreground md:text-4xl">
                From Discovery To Multi-Site Scale
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                One consistent lifecycle from the first conversation through expansion.
              </p>
            </div>
            <ol className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {LIFECYCLE.map((item, index) => (
                <li
                  key={item.step}
                  className="rounded-[20px] border border-[hsl(var(--line))] bg-card/50 p-5 transition-colors hover:border-primary/40"
                >
                  <span className="text-[11px] font-semibold tracking-[0.2em] text-muted-foreground">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="font-orbitron mt-1 text-lg font-bold text-foreground">{item.step}</p>
                  <p className="mt-1 text-sm text-primary">{item.detail}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="healthcare-assessment" className="scroll-mt-32 px-4 pb-16" aria-labelledby="healthcare-assessment-heading">
          <h2 id="healthcare-assessment-heading" className="sr-only">
            Healthcare N.E.I.L. Assessment Form
          </h2>
          <HealthcareAssessmentForm />
        </section>

        <section className="px-4 pb-16" aria-labelledby="why-method-heading">

          <div className="mx-auto max-w-7xl">
            <h2 id="why-method-heading" className="font-orbitron text-center text-3xl font-bold text-foreground md:text-4xl">
              Why The Process Matters
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {trustPoints.map((point) => {
                const Icon = point.icon;
                return (
                  <div
                    key={point.title}
                    className="rounded-[22px] border border-[hsl(var(--line))] bg-card/50 p-6 transition-all hover:border-[#D6A84F]/40"
                  >
                    <Icon className="h-6 w-6 text-[#D6A84F]" />
                    <h3 className="font-orbitron mt-4 text-base font-bold tracking-wide text-foreground">
                      {point.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{point.copy}</p>
                  </div>
                );
              })}
            </div>
            <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-muted-foreground">
              Neil AI does not guarantee specific financial or operational outcomes. Projections shared
              during discovery are based on customer-provided and customer-confirmed data, and estimates
              are identified as estimates.
            </p>
          </div>
        </section>
      </main>
      <section className="mx-auto w-full max-w-7xl px-[clamp(1rem,5vw,1.5rem)] pb-16">
        <a href="/robots/humanoid/healthcare" className="block rounded-2xl border border-[hsl(var(--line))] glass-effect p-6 hover:border-primary/50 transition-colors">
          <p className="text-xs uppercase tracking-wide text-accent">Humanoid Division</p>
          <p className="mt-2 font-orbitron text-lg font-bold">Humanoid Robotics for Healthcare &amp; Senior Care</p>
          <p className="mt-2 text-sm text-muted-foreground">Connected care technology, service humanoids, AI automation and Robotics-as-a-Service for hospitals and senior-living communities.</p>
        </a>
      </section>

      <Footer />
    </div>
  );
};

export default NeilMethod;
